import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import {
  COMPOSITION_GET_DATA_V2,
  PositionsEntityKind,
} from '@revelio/data-access';

import type { AdjacentRole } from './useAdjacentRoles';
import type { ResolvedSkill } from './useMapUserSkills';
import type { RoleGranularity } from './useResolveProfileIds';

/** A skill common in an adjacent role with whether the user already has it */
export interface RoleSkillComparison {
  name: string;
  /** Share of people in this role who have this skill (0-100 from API) */
  share: number;
  /** Whether the user already has this skill */
  userHas: boolean;
}

/** Skill comparison data for a single adjacent role */
export interface AdjacentRoleSkillData {
  roleId: number;
  /** Top skills in this role, sorted by share descending */
  topSkills: RoleSkillComparison[];
  /** Skills common in this role that the user doesn't have */
  skillGaps: RoleSkillComparison[];
  /** Skills the user has that are also common in this role */
  sharedSkills: RoleSkillComparison[];
}

interface UseAdjacentRoleSkillsResult {
  skillsByRole: Map<number, AdjacentRoleSkillData>;
  isLoading: boolean;
  progress: number;
}

const MAX_ROLES_TO_ENRICH = 7;
const TOP_SKILLS_LIMIT = 15;
const MIN_SKILL_SHARE = 1; // Minimum share % to include a skill

const ROLE_GRANULARITY_TO_ENTITY: Record<RoleGranularity, PositionsEntityKind> =
  {
    role_k15000: PositionsEntityKind.RoleK15000,
    role_k1500: PositionsEntityKind.RoleK1500,
    role_k150: PositionsEntityKind.RoleK150,
    role_k7: PositionsEntityKind.RoleK7,
  };

export const useAdjacentRoleSkills = (
  adjacentRoles: AdjacentRole[],
  userSkills: ResolvedSkill[],
  roleGranularity: RoleGranularity = 'role_k150'
): UseAdjacentRoleSkillsResult => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<Map<number, AdjacentRoleSkillData>>(
    new Map()
  );
  const prevRolesRef = useRef<string>('');

  const rolesToEnrich = adjacentRoles.slice(0, MAX_ROLES_TO_ENRICH);

  // Build a set of user skill names for fast lookup
  const userSkillNameSet = useMemo(
    () => new Set(userSkills.map((s) => s.name.toLowerCase().trim())),
    [userSkills]
  );

  // Reset when adjacent roles change
  const rolesKey = rolesToEnrich.map((r) => r.id).join(',');
  useEffect(() => {
    if (rolesKey !== prevRolesRef.current) {
      prevRolesRef.current = rolesKey;
      setCurrentIndex(0);
      setResults(new Map());
    }
  }, [rolesKey]);

  const currentRole =
    currentIndex < rolesToEnrich.length
      ? rolesToEnrich[currentIndex]
      : undefined;

  const entityType =
    ROLE_GRANULARITY_TO_ENTITY[roleGranularity] ?? PositionsEntityKind.RoleK150;

  const compositionVariables = useMemo(() => {
    if (!currentRole || currentIndex >= rolesToEnrich.length) {
      return undefined;
    }

    return {
      entities: [
        {
          EntID: currentRole.id,
          EntType: entityType,
        },
      ],
      filters: { raw: false },
      breakdownOptions: {
        Skill: { all: true },
      },
    };
  }, [currentRole, currentIndex, rolesToEnrich.length, entityType]);

  const [{ data, fetching, error }] = useQuery({
    query: COMPOSITION_GET_DATA_V2,
    variables: compositionVariables ?? {
      entities: [],
      filters: { raw: false },
    },
    pause: !compositionVariables,
  });

  // When data arrives, process skill comparison and advance
  useEffect(() => {
    if (!currentRole || fetching || currentIndex >= rolesToEnrich.length) {
      return;
    }

    if (data || error) {
      const skillData = processSkillData(
        currentRole.id,
        data,
        userSkillNameSet
      );

      setResults((prev) => {
        const next = new Map(prev);
        next.set(currentRole.id, skillData);
        return next;
      });
      setCurrentIndex((prev) => prev + 1);
    }
  }, [
    data,
    error,
    fetching,
    currentRole,
    currentIndex,
    rolesToEnrich.length,
    userSkillNameSet,
  ]);

  const isLoading =
    rolesToEnrich.length > 0 && currentIndex < rolesToEnrich.length;

  const progress =
    rolesToEnrich.length > 0 ? currentIndex / rolesToEnrich.length : 1;

  return {
    skillsByRole: results,
    isLoading,
    progress,
  };
};

/** Extract and compare skills from CompositionV2 response */
function processSkillData(
  roleId: number,
  data: unknown,
  userSkillNames: Set<string>
): AdjacentRoleSkillData {
  const empty: AdjacentRoleSkillData = {
    roleId,
    topSkills: [],
    skillGaps: [],
    sharedSkills: [],
  };

  const compositionData = data as {
    composition_v2?: {
      entities?: Array<{
        metrics?: {
          skills?: Array<{
            metadata?: {
              short_name?: string | null;
              long_name?: string | null;
            } | null;
            timeseries?: Array<{
              share?: number | null;
            } | null> | null;
          } | null> | null;
        } | null;
      } | null> | null;
    } | null;
  } | null;

  const entity = compositionData?.composition_v2?.entities?.[0];
  const skills = entity?.metrics?.skills;
  if (!skills) return empty;

  const allSkills: RoleSkillComparison[] = skills
    .filter(
      (s): s is NonNullable<typeof s> => s !== null && s.metadata !== null
    )
    .map((skill) => {
      const name =
        skill.metadata?.long_name || skill.metadata?.short_name || '';
      const ts = (skill.timeseries ?? []).filter(
        (t): t is NonNullable<typeof t> => t !== null
      );
      const latestShare = ts.length > 0 ? (ts[ts.length - 1]?.share ?? 0) : 0;
      const userHas = userSkillNames.has(name.toLowerCase().trim());

      return { name, share: latestShare, userHas };
    })
    .filter((s) => s.name && s.share >= MIN_SKILL_SHARE)
    .sort((a, b) => b.share - a.share);

  const topSkills = allSkills.slice(0, TOP_SKILLS_LIMIT);
  const skillGaps = topSkills.filter((s) => !s.userHas);
  const sharedSkills = topSkills.filter((s) => s.userHas);

  return {
    roleId,
    topSkills,
    skillGaps,
    sharedSkills,
  };
}
